import styled, { css } from 'styled-components';
import MaskedInput from 'react-text-mask';
import { Typography } from '@/components/UI/Typography/Typography.styles';

export const requiredMark = css`
  &::after {
    content: '*';
    color: ${({ theme }) => theme.palette.brand.main};
    font-size: 14px;
    font-weight: 400;
    margin-left: 3px;
  }
`;

const baseInputStyles = css`
  color: ${({ theme }) => theme.palette.primary.labels};
  font-size: 16px;
  font-weight: 400;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.palette.primary.border};
  background: ${({ theme }) => theme.palette.secondary.background};
  padding: 28px 15px 10px;
  margin-bottom: 25px;
  height: 54px;
  width: 100%;

  &:focus,
  &:not(:placeholder-shown) {
    background: ${({ theme }) => theme.palette.secondary.background};
  }

  &:focus {
    outline: none;
  }

  &:not(:placeholder-shown) ~ label,
  &:focus ~ label {
    top: 10px;
    font-size: 14px;
    pointer-events: none;
    transform: none;
    background-color: transparent;
  }
`;

export const BusinessPageForm = styled.div`
  border-radius: 21px;
  background: ${({ theme }) => theme.palette.primary.background};
  padding: 40px 35px;
  flex: 0 0 calc(50% - 20px);
  min-width: 0;
  box-sizing: border-box;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex: 1 1 100%;
    flex-direction: column;
    align-items: center;
    margin: 0;
    width: 100%;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 25px 15px;
  }
`;

export const BusinessPageFormTitle = styled(Typography.Header)`
  margin-bottom: 25px;
  font-size: 24px;
  font-weight: 700;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 20px;
  }
`;

export const BusinessPageFormItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

export const BusinessPageFormItemsGroup = styled.div`
  position: relative;
`;

export const BusinessPageCaregories = styled.div`
  position: relative;
`;

export const BusinessPageFormLabel = styled.label<{
  $showRequired?: boolean;
}>`
  color: ${({ theme }) => theme.palette.secondary.labels};
  font-size: 16px;
  font-weight: 400;
  position: absolute;
  pointer-events: none;
  left: 16px;
  top: 18px;
  transition: 300ms ease all;
  ${({ $showRequired = true }) => $showRequired && requiredMark}
`;

export const BusinessPageFormItems = styled.input<{ $hasError?: boolean }>`
  ${baseInputStyles}

  ${({ theme, $hasError }) =>
    $hasError &&
    css`
      border-color: ${theme.palette.system.red};
      background: ${theme.palette.system.redToWhite};

      &:focus {
        border-color: ${theme.palette.system.red};
        background: ${theme.palette.system.redToWhite};
      }
    `}
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const BusinessPageFormItemsPhone = styled(MaskedInput as any)<{
  $hasError?: boolean;
}>`
  ${baseInputStyles}

  ${({ theme, $hasError }) =>
    $hasError &&
    css`
      border-color: ${theme.palette.system.red};
      background: ${theme.palette.system.redToWhite};

      &:focus {
        border-color: ${theme.palette.system.red};
        background: ${theme.palette.system.redToWhite};
      }
    `}
`;

export const BusinessPageFormItemsCategory = styled.div<{
  $showModal?: boolean;
  $showNames?: boolean;
  $hasError?: boolean;
}>`
  color: ${({ theme }) => theme.palette.primary.labels};
  font-size: 16px;
  font-weight: 400;
  border-radius: 14px;
  border: 1px solid
    ${({ theme, $showModal, $hasError }) => {
      if ($hasError) return theme.palette.system.red;
      if ($showModal) return theme.palette.brand.main;
      return theme.palette.primary.border;
    }};
  background: ${({ theme, $hasError }) => {
    if ($hasError) return theme.palette.system.redToWhite;
    return theme.palette.secondary.background;
  }};
  padding: 28px 40px 10px 15px;
  margin-bottom: 25px;
  height: 54px;
  width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  position: relative;
  &::after {
    content: '';
    background-image: url('/images/business/arrow-bottom.svg');
    position: absolute;
    width: 17px;
    height: 10px;
    background-repeat: no-repeat;
    background-position: center;
    right: 15px;
    top: 21px;
  }
  ${({ $showModal, $hasError }) =>
    $showModal &&
    !$hasError &&
    css`
      outline: none;
      border: 1px solid ${({ theme }) => theme.palette.brand.main};
      background: ${({ theme }) => theme.palette.secondary.background};

      &::after {
        background-image: url('/images/business/arrow.svg');
      }
    `}

  ${({ $showModal, $showNames }) =>
    ($showModal || $showNames) &&
    css`
      & ~ label {
        top: 10px;
        font-size: 14px;
        pointer-events: none;
        transform: none;
        background-color: transparent;
      }
    `}
`;

export const BusinessPageFormError = styled.span`
  color: ${({ theme }) => theme.palette.system.red};
  font-size: 14px;
  font-weight: 400;
  position: relative;
  left: 16px;
  bottom: 25px;
`;

export const BusinessPageFormButton = styled.button`
  border-radius: 14px;
  background: ${({ theme }) => theme.palette.brand.main};
  color: ${({ theme }) => theme.palette.primary.background};
  text-align: center;
  font-size: 16px;
  font-weight: 400;
  margin-top: 15px;
  padding: 15px 35px;
  width: 100%;
  border: 0;
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.palette.brand.main};
    opacity: 0.9;
  }

  @media (max-width: ${({ theme }) =>
      theme.breakpoints.lg}) and (min-width: 949px) {
    margin-top: 5px;
  }
`;

export const BusinessPageFormContent = styled.div`
  color: ${({ theme }) => theme.palette.secondary.labels};
  font-size: 14px;
  font-weight: 400;
  margin-top: 20px;
  text-align: center;
  a {
    color: ${({ theme }) => theme.palette.system.blue};
  }
`;
